import React, { useEffect, useMemo, useState } from "react";
import { Printer, ChevronDown, ChevronRight } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Label } from "../ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../ui/table";
import { Skeleton } from "../ui/skeleton";
import { EmptyState } from "../shared/EmptyState";
import { ErrorState } from "../shared/ErrorState";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { todayLocal, formatDate } from "../../lib/utils";
import { useListState } from "../../hooks/useListState";

const TYPE_LABELS = { "0": "All (Delivery + Pickup)", "2": "Delivery", "1": "Pickup" };

function printPackingList() {
  document.body.classList.add("printing-packing");
  const cleanup = () => {
    document.body.classList.remove("printing-packing");
    window.removeEventListener("afterprint", cleanup);
  };
  window.addEventListener("afterprint", cleanup);
  window.print();
  setTimeout(cleanup, 1000);
}

export function PackingListTab() {
  const [date, setDate] = useListState("reports.packing.date", todayLocal());
  const [type, setType] = useListState("reports.packing.type", "0");
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [showCustomers, setShowCustomers] = useState(false);

  const load = () => {
    setState({ loading: true, error: null, data: null });
    apiClient.get("/reports/packing", { params: { date, type } })
      .then(({ data }) => setState({ loading: false, error: null, data }))
      .catch((error) => setState({ loading: false, error: getApiErrorMessage(error, "Could not load the packing report."), data: null }));
  };

  useEffect(load, [date, type]);

  const r = state.data;
  const itemColumns = useMemo(() => {
    if (!r) return [];
    const set = new Set();
    r.customers.forEach((c) => Object.keys(c.items || {}).forEach((k) => set.add(k)));
    return Array.from(set).sort();
  }, [r]);

  const totals = r ? r.totals.filter((t) => t.quantity > 0) : [];

  return (
    <div className="mt-4 space-y-4" data-testid="packing-list-tab">
      <div className="flex flex-wrap items-end gap-3 print:hidden">
        <div className="space-y-1.5">
          <Label htmlFor="packing-date">Date</Label>
          <Input id="packing-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="packing-date-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="packing-type">Type</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger id="packing-type" className="w-52" data-testid="packing-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">All (Delivery + Pickup)</SelectItem>
              <SelectItem value="2">Delivery</SelectItem>
              <SelectItem value="1">Pickup</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" className="ml-auto" onClick={printPackingList} data-testid="packing-print-button">
          <Printer size={14} strokeWidth={1.5} /> Print packing list
        </Button>
      </div>

      {state.error ? (
        <ErrorState message={state.error} onRetry={load} testId="packing-error" />
      ) : state.loading ? (
        <Skeleton className="h-64 w-full" />
      ) : r.subscription_count === 0 ? (
        <EmptyState title="Nothing to pack" description="No eligible subscriptions for this date/type." testId="packing-empty" />
      ) : (
        <>
          {/* Printable packing list (A4) — the only thing that prints */}
          <Card className="packing-print" data-testid="packing-totals-card">
            <CardContent className="p-6">
              <div className="mb-5 border-b border-border pb-4">
                <h2 className="font-heading text-2xl font-extrabold text-text-main">Packing List</h2>
                <p className="mt-1 text-sm text-text-muted">
                  {formatDate(date)} · {TYPE_LABELS[type] || "All"} · <span className="font-semibold text-text-main">{r.subscription_count}</span> customers
                </p>
              </div>
              <div className="grid grid-cols-2 gap-x-32 gap-y-5" data-testid="packing-totals-grid">
                {totals.map((t) => (
                  <div
                    key={t.item}
                    className="flex items-baseline gap-6 border-b border-dashed border-border pb-3"
                    data-testid={`packing-total-${t.item.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <span className="flex-1 text-base font-medium text-text-main">{t.item}</span>
                    <span className="w-16 text-right font-heading text-2xl font-extrabold tabular-nums text-primary">{t.quantity}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Per-customer breakdown — collapsed by default, never printed */}
          <Card className="print:hidden" data-testid="packing-customers-card">
            <CardContent className="p-0">
              <button
                type="button"
                onClick={() => setShowCustomers((v) => !v)}
                className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
                data-testid="packing-customers-toggle"
              >
                <span className="font-heading text-sm font-semibold text-text-main">
                  Customer breakdown ({r.customers.length})
                </span>
                {showCustomers ? <ChevronDown size={18} strokeWidth={1.75} className="text-text-muted" /> : <ChevronRight size={18} strokeWidth={1.75} className="text-text-muted" />}
              </button>
              {showCustomers && (
                <div className="overflow-x-auto border-t border-border" data-testid="packing-customers-table-wrap">
                  <Table data-testid="packing-customers-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        {itemColumns.map((c) => <TableHead key={c}>{c}</TableHead>)}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {r.customers.map((c) => (
                        <TableRow key={c.subscription_id} data-testid={`packing-row-${c.subscription_id}`}>
                          <TableCell className="font-medium">{c.customer_name || `Sub #${c.subscription_id}`}</TableCell>
                          {itemColumns.map((col) => <TableCell key={col}>{c.items?.[col] ?? "—"}</TableCell>)}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
